import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Assessment as ReportIcon,
  LocationOn as LocationIcon,
  Campaign as CampaignIcon,
  Visibility as ImpressionIcon,
  ViewModule as SlotIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { reportAPI } from '../services/api';

const Reports = () => {
  const [loading, setLoading] = useState({});
  const [globalStartDate, setGlobalStartDate] = useState('');
  const [globalEndDate, setGlobalEndDate] = useState('');
  const [dateFilters, setDateFilters] = useState({
    slotAvailability: { startDate: '', endDate: '' },
    campaignPerformance: { startDate: '', endDate: '' },
    impressions: { startDate: '', endDate: '' },
    locationSummary: {},
  });

  const applyGlobalFilter = () => {
    setDateFilters({
      ...dateFilters,
      slotAvailability: { startDate: globalStartDate, endDate: globalEndDate },
      campaignPerformance: { startDate: globalStartDate, endDate: globalEndDate },
      impressions: { startDate: globalStartDate, endDate: globalEndDate },
    });
  };

  const clearGlobalFilter = () => {
    setGlobalStartDate('');
    setGlobalEndDate('');
    setDateFilters({
      slotAvailability: { startDate: '', endDate: '' },
      campaignPerformance: { startDate: '', endDate: '' },
      impressions: { startDate: '', endDate: '' },
      locationSummary: {},
    });
  };

  const handleDateChange = (reportType, field, value) => {
    setDateFilters({
      ...dateFilters,
      [reportType]: {
        ...dateFilters[reportType],
        [field]: value,
      },
    });
  };

  const downloadReport = async (reportType, apiFunction, filename) => {
    try {
      setLoading({ ...loading, [reportType]: true });
      
      const params = dateFilters[reportType];
      const response = await apiFunction(params);
      
      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error(error.response?.data?.message || 'Failed to download report');
    } finally {
      setLoading({ ...loading, [reportType]: false });
    }
  };

  const reports = [
    {
      id: 'slotAvailability',
      title: 'Slot Availability Report',
      description: 'View all screens, slots, and their availability status with campaign details',
      icon: <SlotIcon sx={{ fontSize: 48, color: '#1976d2' }} />,
      color: '#e3f2fd',
      apiFunction: reportAPI.downloadSlotAvailability,
      filename: 'slot_availability_report',
      hasDateFilter: true,
    },
    {
      id: 'campaignPerformance',
      title: 'Campaign Performance Report',
      description: 'Detailed campaign metrics including impressions, duration, and status',
      icon: <CampaignIcon sx={{ fontSize: 48, color: '#2e7d32' }} />,
      color: '#e8f5e9',
      apiFunction: reportAPI.downloadCampaignPerformance,
      filename: 'campaign_performance_report',
      hasDateFilter: true,
    },
    {
      id: 'impressions',
      title: 'Impressions Report',
      description: 'Daily impression logs by campaign, screen, and slot',
      icon: <ImpressionIcon sx={{ fontSize: 48, color: '#ed6c02' }} />,
      color: '#fff3e0',
      apiFunction: reportAPI.downloadImpressions,
      filename: 'impressions_report',
      hasDateFilter: true,
    },
    {
      id: 'locationSummary',
      title: 'Location Summary Report',
      description: 'Location-wise statistics with screen counts and slot occupancy',
      icon: <LocationIcon sx={{ fontSize: 48, color: '#9c27b0' }} />,
      color: '#f3e5f5',
      apiFunction: reportAPI.downloadLocationSummary,
      filename: 'location_summary_report',
      hasDateFilter: false,
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <ReportIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Typography variant="h4" fontWeight="bold">
          Reports & Analytics
        </Typography>
      </Box>

      {/* Info Panel */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: 3,
          backgroundColor: '#eff6ff',
          borderColor: '#3b82f6',
          '& .MuiAlert-icon': {
            color: '#3b82f6',
          }
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          📊 All reports are generated in Excel format (.xlsx) with professional formatting
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          Date filters are optional — leave blank to download all available data. Files are named with the current date for easy organization.
        </Typography>
      </Alert>

      {/* Global Date Filter */}
      <Paper sx={{ p: 2.5, mb: 3, backgroundColor: '#fafbfc', border: '1px solid #e2e8f0' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1e293b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>
          📅 Optional Global Date Filter
        </Typography>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={globalStartDate}
              onChange={(e) => setGlobalStartDate(e.target.value)}
              helperText="Applied to: Slot Availability, Campaign Performance, Impressions"
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={globalEndDate}
              onChange={(e) => setGlobalEndDate(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
            />
          </Grid>
          <Grid item xs={12} sm={2} sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Apply dates to all reports with date filters" arrow>
              <Button
                variant="contained"
                size="small"
                onClick={applyGlobalFilter}
                disabled={!globalStartDate || !globalEndDate}
                sx={{
                  backgroundColor: '#7C3AED',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#6D28D9' },
                  flex: 1,
                }}
              >
                Apply
              </Button>
            </Tooltip>
            <Tooltip title="Clear all date filters" arrow>
              <Button
                variant="outlined"
                size="small"
                onClick={clearGlobalFilter}
                sx={{
                  borderColor: '#cbd5e1',
                  color: '#64748b',
                  textTransform: 'none',
                }}
              >
                Clear
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Report Cards */}
      <Grid container spacing={3}>
        {reports.map((report) => (
          <Grid item xs={12} md={6} key={report.id}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                <Box
                  sx={{
                    backgroundColor: report.color,
                    borderRadius: 1,
                    p: 1,
                    mb: 1.5,
                    display: 'flex',
                    justifyContent: 'center',
                    opacity: 0.8,
                  }}
                >
                  {report.icon}
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#1e293b', fontSize: '1.1rem' }}>
                  {report.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  {report.description}
                </Typography>

                {report.hasDateFilter && (
                  <Box sx={{ mt: 1.5, p: 1, backgroundColor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 1 }}>
                      Optional: Override global dates for this report
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          label="Start"
                          InputLabelProps={{ shrink: true }}
                          value={dateFilters[report.id]?.startDate || ''}
                          onChange={(e) =>
                            handleDateChange(report.id, 'startDate', e.target.value)
                          }
                          sx={{ 
                            '& .MuiOutlinedInput-root': { 
                              fontSize: '0.85rem',
                              backgroundColor: '#fff',
                            } 
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          label="End"
                          InputLabelProps={{ shrink: true }}
                          value={dateFilters[report.id]?.endDate || ''}
                          onChange={(e) =>
                            handleDateChange(report.id, 'endDate', e.target.value)
                          }
                          sx={{ 
                            '& .MuiOutlinedInput-root': { 
                              fontSize: '0.85rem',
                              backgroundColor: '#fff',
                            } 
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </CardContent>
              <CardActions sx={{ p: 1.5, pt: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={
                    loading[report.id] ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={() =>
                    downloadReport(report.id, report.apiFunction, report.filename)
                  }
                  disabled={loading[report.id]}
                  sx={{
                    backgroundColor: '#7C3AED',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: '#6D28D9' },
                  }}
                >
                  {loading[report.id] ? 'Downloading...' : 'Download'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Reports;